import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Headphones, KeyRound, LogOut, Package, Play, ShoppingBag, Smartphone, User } from "lucide-react";
import { api } from "../api";
import { toast } from "sonner";

const APP_LINKS = { "digilingua-hiszpanski": "/learn/", "digikids-matematyka": "/kids/" };
const FORMAT_ICONS = { ebook: BookOpen, audiobook: Headphones, aplikacja: Smartphone };
const STATUS_LABELS = {
  "nowe": "Nowe", "oplacone": "Opłacone", "w-realizacji": "W realizacji",
  "zrealizowane": "Zrealizowane", "anulowane": "Anulowane",
};
const PAY_LABELS = {
  paid: "Opłacone", pending: "Oczekuje na płatność", "oczekuje-na-przelew": "Oczekuje na przelew",
  failed: "Nieudana", expired: "Wygasła",
};

export default function CustomerPanel() {
  const [token, setToken] = useState(() => localStorage.getItem("dl_customer_token") || "");
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dl_customer_user") || "null"); } catch { return null; }
  });
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    if (!token) return;
    api.get("/customer/orders", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setOrders(r.data))
      .catch(() => setOrders([]));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = mode === "login"
        ? await api.post("/auth/login", { email, password })
        : await api.post("/auth/register", { email, password, name });
      localStorage.setItem("dl_customer_token", data.token);
      localStorage.setItem("dl_customer_user", JSON.stringify(data.user));
      setToken(data.token);
      setUser(data.user);
      toast.success(mode === "login" ? "Witaj ponownie!" : "Konto utworzone — miłych zakupów!");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Nie udało się — sprawdź dane i spróbuj ponownie.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("dl_customer_token");
    localStorage.removeItem("dl_customer_user");
    setToken("");
    setUser(null);
    setOrders(null);
  };

  if (!token) {
    return (
      <div className="mx-auto max-w-md px-4 pb-24 pt-28" data-testid="customer-auth">
        <div className="rounded-3xl glass-panel p-8">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-500 to-fuchsia-500 shadow-lg shadow-cyan-500/30">
            <User className="h-7 w-7 text-white" />
          </div>
          <h1 className="mt-5 text-center font-display text-2xl font-extrabold">Panel klienta</h1>
          <p className="mt-2 text-center text-sm text-slate-400">
            Zaloguj się lub załóż konto, aby zobaczyć historię zakupów i swoje produkty.
          </p>

          <div className="mt-6 grid grid-cols-2 gap-2 rounded-xl bg-slate-900 p-1.5" data-testid="auth-mode-tabs">
            <button onClick={() => { setMode("login"); setError(""); }} data-testid="auth-tab-login"
              className={`rounded-lg py-2 text-sm font-bold transition-colors ${mode === "login" ? "bg-gradient-to-r from-cyan-500 to-teal-500 text-slate-950" : "text-slate-400"}`}>
              Logowanie
            </button>
            <button onClick={() => { setMode("register"); setError(""); }} data-testid="auth-tab-register"
              className={`rounded-lg py-2 text-sm font-bold transition-colors ${mode === "register" ? "bg-gradient-to-r from-cyan-500 to-teal-500 text-slate-950" : "text-slate-400"}`}>
              Rejestracja
            </button>
          </div>

          <form onSubmit={submit} className="mt-5 space-y-4">
            {mode === "register" && (
              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Imię</label>
                <input value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name"
                  className="w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-sm text-slate-200 outline-none focus:border-cyan-500" placeholder="Twoje imię" />
              </div>
            )}
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="auth-email"
                className="w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-sm text-slate-200 outline-none focus:border-cyan-500" placeholder="twoj@email.pl" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">Hasło</label>
              <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="auth-password"
                className="w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-sm text-slate-200 outline-none focus:border-cyan-500" placeholder="min. 6 znaków" />
            </div>
            {error && <div className="rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-xs font-semibold text-rose-300" data-testid="auth-error">{error}</div>}
            <button type="submit" disabled={loading} data-testid="auth-submit"
              className="w-full rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 py-3.5 font-display text-sm font-bold text-slate-950 shadow-lg shadow-cyan-500/30 transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-40">
              {loading ? "Chwileczkę..." : mode === "login" ? "Zaloguj się" : "Utwórz konto"}
            </button>
          </form>
          <p className="mt-4 text-center text-[11px] leading-relaxed text-slate-500">
            Zamówienia łączą się z kontem automatycznie po adresie e-mail podanym przy zakupie.
          </p>
        </div>
      </div>
    );
  }

  const paidOrders = (orders || []).filter((o) => o.payment_status === "paid");
  const activeItems = paidOrders.flatMap((o) => o.items.map((i) => ({ ...i, order_id: o.id, creds: o.delivery_credentials })));
  const spent = paidOrders.reduce((s, o) => s + o.total, 0);

  return (
    <div className="mx-auto max-w-5xl px-4 pb-24 pt-28 sm:px-6" data-testid="customer-panel">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Witaj, <span className="gradient-text">{user?.name || user?.email}</span></h1>
          <p className="mt-1 text-sm text-slate-400">{user?.email} · konto klienta</p>
        </div>
        <button onClick={logout} data-testid="customer-logout" className="flex items-center gap-2 rounded-xl border border-slate-700 px-4 py-2.5 text-sm font-semibold text-slate-300 transition-colors hover:border-rose-400/50 hover:text-rose-300">
          <LogOut className="h-4 w-4" /> Wyloguj
        </button>
      </div>

      <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3" data-testid="customer-stats">
        <div className="rounded-2xl glass-panel p-5">
          <ShoppingBag className="h-5 w-5 text-cyan-400" />
          <div className="mt-2 font-display text-2xl font-extrabold">{(orders || []).length}</div>
          <div className="text-xs text-slate-500">zamówień</div>
        </div>
        <div className="rounded-2xl glass-panel p-5">
          <Package className="h-5 w-5 text-fuchsia-400" />
          <div className="mt-2 font-display text-2xl font-extrabold">{activeItems.length}</div>
          <div className="text-xs text-slate-500">aktywnych produktów</div>
        </div>
        <div className="rounded-2xl glass-panel p-5 max-sm:col-span-2">
          <KeyRound className="h-5 w-5 text-teal-400" />
          <div className="mt-2 font-display text-2xl font-extrabold">{spent.toFixed(2)} zł</div>
          <div className="text-xs text-slate-500">wartość zakupów</div>
        </div>
      </div>

      <h2 className="mt-10 font-display text-xl font-bold">Twoje aktywne produkty</h2>
      {orders === null ? (
        <p className="mt-4 text-sm text-slate-500">Ładowanie...</p>
      ) : activeItems.length === 0 ? (
        <div className="mt-4 rounded-2xl glass-panel p-8 text-center" data-testid="no-products">
          <p className="text-sm text-slate-400">Brak aktywnych produktów. Opłacone zamówienia pojawią się tutaj automatycznie.</p>
          <Link to="/" className="mt-4 inline-block rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 px-6 py-3 font-display text-sm font-bold text-slate-950">Przeglądaj katalog</Link>
        </div>
      ) : (
        <div className="mt-4 grid gap-4 sm:grid-cols-2" data-testid="active-products">
          {activeItems.map((i, idx) => {
            const Icon = FORMAT_ICONS[i.format] || BookOpen;
            const link = APP_LINKS[i.product_id] || `/produkt/${i.product_id}`;
            return (
              <div key={idx} className="flex gap-4 rounded-2xl glass-panel p-4" data-testid={`active-product-${i.product_id}`}>
                {i.cover_image ? (
                  <img src={i.cover_image} alt={i.title} className="h-24 w-18 shrink-0 rounded-xl object-cover shadow-lg" style={{ width: 72 }} />
                ) : (
                  <div className="flex h-24 w-18 shrink-0 items-center justify-center rounded-xl bg-cyan-500/15 text-cyan-400" style={{ width: 72 }}>
                    <Icon className="h-7 w-7" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-bold text-slate-100">{i.title}</div>
                  <div className="text-xs text-slate-500">{i.format} · zam. {i.order_id}</div>
                  <a href={link} target={link.startsWith("/") ? "_self" : "_blank"} rel="noreferrer" data-testid={`open-product-${i.product_id}`}
                    className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-cyan-500 to-teal-500 px-4 py-2 text-xs font-bold text-slate-950 transition-transform hover:scale-105">
                    <Play className="h-3.5 w-3.5" /> {i.format === "aplikacja" ? "Otwórz aplikację" : "Pobierz / czytaj"}
                  </a>
                  {i.format === "aplikacja" && i.creds && (
                    <div className="mt-2 rounded-lg border border-cyan-500/25 bg-cyan-500/10 px-3 py-2 text-[11px] leading-relaxed text-cyan-200" data-testid={`creds-${i.product_id}`}>
                      Login: <b>{i.creds.email}</b>{i.creds.password ? <> · Hasło: <b className="text-amber-300">{i.creds.password}</b></> : " · hasło z pierwszego zakupu"}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <h2 className="mt-10 font-display text-xl font-bold">Historia zamówień</h2>
      {orders && orders.length > 0 ? (
        <div className="mt-4 space-y-3" data-testid="orders-history">
          {orders.map((o) => (
            <div key={o.id} className="rounded-2xl glass-panel p-4" data-testid={`order-${o.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <span className="font-mono text-sm font-bold text-cyan-300">{o.id}</span>
                  <span className="ml-3 text-xs text-slate-500">{o.created_at.slice(0, 10)}</span>
                  <span className="ml-3 rounded-full bg-slate-800 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    {o.payment_method === "transfer" ? "Przelew" : "Karta/BLIK/P24"}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`rounded-full border px-3 py-1 text-[11px] font-bold ${o.payment_status === "paid" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-amber-500/40 bg-amber-500/10 text-amber-300"}`}>
                    {PAY_LABELS[o.payment_status] || o.payment_status}
                  </span>
                  <span className="font-display text-sm font-extrabold">{o.total.toFixed(2)} zł</span>
                </div>
              </div>
              <div className="mt-2.5 text-xs text-slate-400">{o.items.map((i) => i.title).join(" · ")}</div>
              <div className="mt-1 text-[11px] text-slate-600">Status: {STATUS_LABELS[o.status] || o.status}{o.delivered_at ? " · produkt wysłany e-mailem" : ""}</div>
            </div>
          ))}
        </div>
      ) : orders && (
        <p className="mt-4 rounded-2xl glass-panel p-8 text-center text-sm text-slate-400" data-testid="no-orders">
          Brak zamówień. Twoje zakupy pojawią się tutaj — również te złożone przed rejestracją, jeśli podałeś ten sam e-mail.
        </p>
      )}
    </div>
  );
}
